import { type Organization } from "../api/client";
import { displayHost, displayUsername, type DecryptedItem } from "../vault/items";
import { TypeIcon, Icons } from "./icons";
import { useT } from "../i18n";

// Dedicated "Connect" screen (bastion landing): a flat list of connectable targets across an org,
// each with a one-click Connect button that opens a clientless brokered session (Lane B). This is
// the discoverable entry point people expect from a jump server — separate from the per-item Launch
// button buried in the vault's details pane. It reuses the same launch pipeline (launchItem), so on
// the web build a click opens the brokered RDP canvas / SSH terminal over the broker WebSocket.
export function ConnectScreen({
  orgs,
  selectedOrg,
  openOrg,
  items,
  launchItem,
}: {
  orgs: Organization[];
  selectedOrg: Organization | null;
  openOrg: (org: Organization) => void;
  items: DecryptedItem[];
  launchItem: (it: DecryptedItem) => boolean;
}) {
  const t = useT();
  // Brokered targets: SSH, RDP, and network_device (web-admin, browser-isolation). Flat, sorted.
  const targets = items
    .filter((it) => it.type === "ssh" || it.type === "rdp" || it.type === "network_device")
    .sort((a, b) => a.content.name.localeCompare(b.content.name));

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-5">
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Icons.rdp className="h-5 w-5 text-accent" />
            {t("Connect")}
          </h1>
          <p className="mt-1 text-sm text-muted">
            {t("Clientless access to your SSH and RDP targets, brokered through the jump server.")}
          </p>
        </header>

        {/* Organization selector — targets are scoped per customer org. */}
        {orgs.length > 0 && (
          <div className="mb-5 flex flex-wrap gap-2">
            {orgs.map((org) => (
              <button
                key={org.id}
                onClick={() => openOrg(org)}
                className={`rounded-full border px-3 py-1 text-sm ${
                  selectedOrg?.id === org.id ? "border-accent bg-accent/10 text-fg" : "border-border text-muted hover:border-accent"
                }`}
              >
                {org.is_personal ? t("Personal") : org.name}
              </button>
            ))}
          </div>
        )}

        {!selectedOrg ? (
          <EmptyState icon={<Icons.vaultPersonal className="h-8 w-8" />} text={t("Select an organization to see its targets.")} />
        ) : targets.length === 0 ? (
          <EmptyState
            icon={<Icons.ssh className="h-8 w-8" />}
            text={t("No SSH or RDP targets in this organization yet. Create one in the Vault (type SSH or RDP).")}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {targets.map((it) => (
              <TargetCard key={it.id} item={it} onConnect={() => launchItem(it)} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TargetCard({ item, onConnect, t }: { item: DecryptedItem; onConnect: () => void; t: (s: string) => string }) {
  const host = displayHost(item.content);
  const user = displayUsername(item.content);
  const kind = item.type === "ssh" ? "SSH" : item.type === "rdp" ? "RDP" : "WEB";
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-panel p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-muted">
          <TypeIcon type={item.type} className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium" title={item.content.name}>
            {item.content.name}
          </div>
          <div className="mt-0.5 truncate font-mono text-xs text-muted" title={host}>
            {user ? `${user}@` : ""}
            {host || t("(no host)")}
          </div>
        </div>
        <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted">{kind}</span>
      </div>
      <button
        onClick={onConnect}
        className="mt-auto rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2"
      >
        ▶ {t("Connect")}
      </button>
    </div>
  );
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="mt-10 flex flex-col items-center gap-3 text-center text-muted">
      <span>{icon}</span>
      <p className="max-w-sm text-sm">{text}</p>
    </div>
  );
}
